/*
  Nature Lane Beauty — announcement bar rotation.

  Crossfades the message blocks on the interval set in the theme editor. The
  fade is CSS; this file only decides which message carries `hidden`.

  No dependencies, and the bar is complete without it — the first message is the
  only visible one until this runs, which is also what a script-blocked visitor
  keeps. With fewer than two messages it binds nothing at all.

  🔒 Deliberately not an aria-live region. These are standing statements, not
  news, and announcing a shipping term over whatever a screen-reader user is
  reading is an interruption with nothing in it. Inactive messages are
  `visibility: hidden`, so exactly one is in the accessibility tree and in the
  tab order at any moment.

  Rotation holds while the bar is hovered, while focus is inside it, while the
  tab is in the background, and while a merchant has a message selected in the
  theme editor. Any hold stops the timer; releasing the last one starts a whole
  fresh interval rather than resuming a part-spent one, so someone who has just
  finished hovering gets the full dwell on the line in front of them.
*/
(function () {
  'use strict';

  // Below this nobody finishes the line before it goes.
  var MIN_INTERVAL = 3000;

  class NlbAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = Array.prototype.slice.call(this.querySelectorAll('[data-nlb-message]'));
      if (this.messages.length < 2) return;

      this.interval = parseInt(this.dataset.interval, 10);
      if (isNaN(this.interval) || this.interval < MIN_INTERVAL) this.interval = MIN_INTERVAL;

      // The server decides which message ships visible; don't assume it is the
      // first one, so a re-render mid-rotation resumes where it left off.
      this.index = 0;
      for (var i = 0; i < this.messages.length; i++) {
        if (!this.messages[i].hasAttribute('hidden')) {
          this.index = i;
          break;
        }
      }

      this.holds = new Set();

      // Listeners on the element itself survive a disconnect, so they are bound
      // once for the element's lifetime; only the document-level one is paired
      // with disconnectedCallback.
      if (!this.bound) {
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.bindSelf();
        this.bound = true;
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (document.hidden) this.holds.add('tab');
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.bound) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    bindSelf() {
      this.addEventListener('mouseenter', () => this.hold('pointer'));
      this.addEventListener('mouseleave', () => this.release('pointer'));

      this.addEventListener('focusin', () => this.hold('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.release('focus');
      });

      // Theme editor: park on the message the merchant has selected, so it does
      // not rotate out from under them while they are typing into it.
      this.addEventListener('shopify:block:select', (event) => {
        var block = event.target.closest ? event.target.closest('[data-nlb-message]') : null;
        var index = this.messages.indexOf(block);
        if (index === -1) return;
        this.hold('editor');
        this.show(index);
      });
      this.addEventListener('shopify:block:deselect', () => this.release('editor'));
    }

    /* ---- Timer ----------------------------------------------------------- */

    start() {
      this.stop();
      if (this.holds.size) return;
      this.timer = window.setInterval(() => {
        this.show((this.index + 1) % this.messages.length);
      }, this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    hold(reason) {
      this.holds.add(reason);
      this.stop();
    }

    release(reason) {
      this.holds.delete(reason);
      this.start();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.hold('tab');
      } else {
        this.release('tab');
      }
    }

    /* ---- Swap ------------------------------------------------------------ */

    show(index) {
      if (index === this.index) return;
      this.messages[this.index].hidden = true;
      this.messages[index].hidden = false;
      this.index = index;
    }
  }

  if (!customElements.get('nlb-announcement-bar')) {
    customElements.define('nlb-announcement-bar', NlbAnnouncementBar);
  }
})();
